import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { colors } from './theme';
import LoadingSpinner from './components/LoadingSpinner';
import SignInPrompt from './components/SignInPrompt';

const API_BASE = process.env.REACT_APP_API_URL;

function normalizeRole(user) {
  const r = user?.publicMetadata?.role || user?.publicMetadata?.roles;
  if (!r) return '';
  return (Array.isArray(r) ? r[0] : String(r)).toLowerCase().trim();
}

const ROLE_LABEL = {
  '': 'No access (guest/customer)',
  service: 'Service',
  'quality-manager': 'Quality manager',
  'production-engineer': 'Production engineer',
  admin: 'Admin',
  gpv: 'GPV', 'test-gpv': 'GPV (test)', 'gpv-rma': 'GPV RMA', ubiqconn: 'Ubiqconn',
};
const roleColor = (r) => (r === 'admin' ? '#ef4444' : r === '' ? colors.textMuted : r.startsWith('service') ? '#46bb89' : '#60a5fa');
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : '—');

export default function AdminUsersPage() {
  const { user, isSignedIn, isLoaded } = useUser();
  const { getToken } = useAuth();
  const role = normalizeRole(user);
  const isAdmin = role === 'admin';
  const isService = role.startsWith('service');
  const canManage = isAdmin || isService;

  const [users, setUsers] = useState([]);
  const [assignable, setAssignable] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      const res = await fetch(`${API_BASE}/admin/users?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Failed to load users (${res.status})`);
      const data = await res.json();
      setUsers(Array.isArray(data.users) ? data.users : []);
      setAssignable(Array.isArray(data.assignableRoles) ? data.assignableRoles : []);
    } catch (e) {
      setError(e.message || 'Failed to load users.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [getToken, search]);

  useEffect(() => {
    if (!canManage) return;
    const t = setTimeout(fetchUsers, 250);
    return () => clearTimeout(t);
  }, [canManage, fetchUsers]);

  const changeRole = async (id, role) => {
    setSavingId(id); setError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/admin/users/${encodeURIComponent(id)}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Update failed (${res.status})`);
      setUsers(prev => prev.map(u => (u.id === id ? { ...u, role: data.role || '' } : u)));
    } catch (e) {
      setError(e.message || 'Failed to update access.');
    } finally {
      setSavingId('');
    }
  };

  if (!isLoaded) return null;
  if (!isSignedIn) return <SignInPrompt />;
  if (!canManage) {
    return (
      <div className="ni-page"><div className="ni-shell ni-shell--narrow">
        <div className="ni-panel ni-empty">Service or admin access required to manage users.</div>
      </div></div>
    );
  }

  const roleOptions = ['', ...assignable];

  return (
    <div className="ni-page">
      <div className="ni-shell">
        <header className="ni-header">
          <div className="ni-brand">
            <Link to="/" title="Home" style={{ display: 'inline-flex', lineHeight: 0 }}>
              <img src="/north-invent-logo.svg" alt="North Invent" className="ni-brand-logo" />
            </Link>
            <div className="ni-brand-copy">
              <span className="ni-eyebrow">Admin · Users</span>
              <span className="ni-brand-subtitle">Grant or revoke access — no Clerk dashboard needed</span>
            </div>
          </div>
          <Link to="/admin/organizations" className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem', textDecoration: 'none' }}>Companies →</Link>
        </header>

        <div className="ni-panel" style={{ marginBottom: '1rem', display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email…" className="ni-input" style={{ flex: 1, minWidth: 240 }}
          />
          <span style={{ fontSize: '0.82rem', color: colors.textMuted }}>{users.length} user{users.length === 1 ? '' : 's'}</span>
        </div>

        {error && <div className="ni-panel" style={{ color: colors.danger, marginBottom: '1rem' }}>{error}</div>}

        {loading ? (
          <div className="ni-panel" style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><LoadingSpinner size={48} /></div>
        ) : (
          <div className="ni-panel" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: colors.textMuted }}>
                  <th style={th}>User</th>
                  <th style={th}>Wants to use</th>
                  <th style={th}>Joined</th>
                  <th style={th}>Last sign-in</th>
                  <th style={th}>Access level</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ borderTop: `1px solid ${colors.panelBorder}` }}>
                    <td style={td}>
                      <div style={{ fontWeight: 700, color: colors.textStrong }}>{u.name || '—'}</div>
                      <div style={{ color: colors.textMuted }}>{u.email}</div>
                    </td>
                    <td style={td}>
                      {u.audience || u.intent || u.company ? (
                        <>
                          <div>{[u.audience, u.intent].filter(Boolean).join(' · ') || '—'}</div>
                          {u.company && <div style={{ color: colors.textMuted }}>{u.company}</div>}
                        </>
                      ) : '—'}
                    </td>
                    <td style={td}>{fmtDate(u.createdAt)}</td>
                    <td style={td}>{fmtDate(u.lastSignInAt)}</td>
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '0.1rem 0.4rem', borderRadius: 5, color: roleColor(u.role), border: `1px solid ${roleColor(u.role)}55`, whiteSpace: 'nowrap' }}>
                          {u.role || 'none'}
                        </span>
                        {(!isAdmin && u.role === 'admin') ? (
                          <span style={{ fontSize: '0.78rem', color: colors.textMuted }}>admin — admin-only</span>
                        ) : (
                          <select
                            value={u.role} disabled={savingId === u.id}
                            onChange={e => changeRole(u.id, e.target.value)}
                            className="ni-select" style={{ minWidth: 170 }}
                          >
                            {(roleOptions.includes(u.role) ? roleOptions : [...roleOptions, u.role]).map(r => (
                              <option key={r || 'none'} value={r}>{ROLE_LABEL[r] || r}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: colors.textMuted }}>No users found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const th = { padding: '0.45rem 0.6rem', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' };
const td = { padding: '0.55rem 0.6rem', color: colors.textBody, verticalAlign: 'top' };
